import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { PickersRangeCalendarHeaderProps } from '@mui/x-date-pickers-pro';
import { useNextMonthDisabled, usePreviousMonthDisabled } from '@mui/x-date-pickers/internals';
import { addMonths, format, getYear, setYear } from 'date-fns';

const YEAR_RANGE = 12;

type DateRangePickerCalendarHeaderProps = PickersRangeCalendarHeaderProps<Date> & {
    onYearSelectOpen?: () => void;
    onYearSelectClose?: () => void;
};

const DateRangePickerCalendarHeader = ({
    calendars,
    currentMonth,
    month,
    monthIndex,
    disableFuture,
    disablePast,
    maxDate,
    minDate,
    onMonthChange,
    timezone,
    onYearSelectOpen,
    onYearSelectClose,
}: DateRangePickerCalendarHeaderProps) => {
    const isNextMonthDisabled = useNextMonthDisabled(currentMonth, {
        disableFuture,
        maxDate,
        timezone,
    });
    const isPreviousMonthDisabled = usePreviousMonthDisabled(currentMonth, {
        disablePast,
        minDate,
        timezone,
    });

    const visibleYear = getYear(month);
    const minYear = getYear(minDate);
    const maxYear = getYear(maxDate);
    const startYear = Math.max(minYear, visibleYear - YEAR_RANGE);
    const endYear = Math.min(maxYear, visibleYear + YEAR_RANGE);
    const yearOptions = Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);

    const changeMonth = (nextMonth: Date, direction: 'left' | 'right') => {
        onMonthChange(nextMonth, direction);
    };

    const handleYearSelect = (event: SelectChangeEvent<number>) => {
        const nextMonth = setYear(month, Number(event.target.value));
        changeMonth(nextMonth, getYear(nextMonth) >= visibleYear ? 'left' : 'right');
    };

    return (
        <Box className="flex items-center justify-between px-4 pb-1 pt-3">
            <Select<number>
                value={visibleYear}
                onChange={handleYearSelect}
                onOpen={onYearSelectOpen}
                onClose={onYearSelectClose}
                size="small"
                variant="standard"
                disableUnderline
                slotProps={{
                    input: {
                        sx: {
                            padding: 0,
                        },
                    },
                }}
                renderValue={(value) => `${format(month, 'MMMM')} ${value}`}
            >
                {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                        {year}
                    </MenuItem>
                ))}
            </Select>
            <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                    onClick={() => changeMonth(addMonths(currentMonth, -1), 'right')}
                    disabled={isPreviousMonthDisabled || monthIndex !== 0}
                    size="small"
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                    onClick={() => changeMonth(addMonths(currentMonth, 1), 'left')}
                    disabled={isNextMonthDisabled || monthIndex !== calendars - 1}
                    size="small"
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Box>
    );
};

export { DateRangePickerCalendarHeader };
